import {
  type barWidgetSchema,
  type comparisonWidgetSchema,
  type donutWidgetSchema,
  type lineWidgetSchema,
  type statsWidgetSchema,
  type tableWidgetSchema,
  type WidgetSource,
  type WidgetSpec,
} from "@growth/schema";
import { type ReactNode } from "react";
import { type z } from "zod";

import { formatValue, niceTicks } from "./format";

/*
 * Static, accessible markup for every widget kind. Rendered on the server for the public blog
 * (crawlable, works without JS) and in the admin editor as the live preview. Colours come from
 * CSS variables (widgets.css), so each brand themes them. widgets.js adds tooltips and legend
 * toggles on top; nothing here depends on it.
 */

type Bar = z.infer<typeof barWidgetSchema>;
type Line = z.infer<typeof lineWidgetSchema>;
type Donut = z.infer<typeof donutWidgetSchema>;
type Stats = z.infer<typeof statsWidgetSchema>;
type Table = z.infer<typeof tableWidgetSchema>;
type Comparison = z.infer<typeof comparisonWidgetSchema>;

const Frame = ({
  spec,
  source,
  children,
}: {
  spec: WidgetSpec;
  source?: WidgetSource;
  children: ReactNode;
}) => (
  <figure className={`cms-widget cms-widget--${spec.kind}`} data-widget-kind={spec.kind}>
    <figcaption className="cms-widget__head">
      <span className="cms-widget__title">{spec.title}</span>
      {spec.subtitle && <span className="cms-widget__subtitle">{spec.subtitle}</span>}
    </figcaption>
    <div className="cms-widget__body">{children}</div>
    {(source || spec.note) && (
      <p className="cms-widget__foot">
        {spec.note && <span className="cms-widget__note">{spec.note}</span>}
        {source && (
          <span className="cms-widget__source">
            Source:{" "}
            <a href={source.url} target="_blank" rel="noopener noreferrer nofollow">
              {source.label}
            </a>
          </span>
        )}
      </p>
    )}
  </figure>
);

// ── Bar (horizontal, HTML) ───────────────────────────────────────────────────────────────────

const BarChart = ({ spec }: { spec: Bar }) => {
  const max = Math.max(...spec.items.map((item) => Math.abs(item.value)), 0) || 1;
  return (
    <Frame spec={spec} source={spec.source}>
      <ul className="cms-bars">
        {spec.items.map((item) => (
          <li
            key={item.label}
            className={item.label === spec.highlight ? "cms-bar is-highlight" : "cms-bar"}
          >
            <span className="cms-bar__label">{item.label}</span>
            {/* SVG attributes, not inline styles: the blog's CSP blocks style="…". */}
            <svg
              className="cms-bar__track"
              viewBox="0 0 100 10"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <rect
                className="cms-bar__fill"
                x="0"
                y="0"
                height="10"
                width={Math.max((Math.abs(item.value) / max) * 100, 1.5)}
              />
            </svg>
            <span className="cms-bar__value">{formatValue(item.value, spec.unit)}</span>
          </li>
        ))}
      </ul>
    </Frame>
  );
};

// ── Line (SVG) ───────────────────────────────────────────────────────────────────────────────

const W = 640;
const H = 300;
const PAD = { top: 16, right: 20, bottom: 36, left: 52 };

const LineChart = ({ spec }: { spec: Line }) => {
  const all = spec.series.flatMap((series) => series.values);
  const ticks = niceTicks(Math.min(...all), Math.max(...all));
  const yMin = ticks[0] ?? 0;
  const yMax = ticks[ticks.length - 1] ?? 1;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const x = (index: number) =>
    PAD.left +
    (spec.xLabels.length === 1 ? innerW / 2 : (index / (spec.xLabels.length - 1)) * innerW);
  const y = (value: number) => PAD.top + innerH - ((value - yMin) / (yMax - yMin || 1)) * innerH;
  const labelEvery = Math.ceil(spec.xLabels.length / 8);
  const summary = spec.series
    .map(
      (s) =>
        `${s.name}: ${s.values.map((v, i) => `${spec.xLabels[i]} ${formatValue(v, spec.unit)}`).join(", ")}`,
    )
    .join("; ");

  return (
    <Frame spec={spec} source={spec.source}>
      {spec.series.length > 1 && (
        <div className="cms-legend" role="group" aria-label="Series">
          {spec.series.map((series, index) => (
            <button
              key={series.name}
              type="button"
              className={`cms-legend__item cms-w-c${index + 1}`}
              data-series-toggle={index}
              aria-pressed="true"
            >
              <span className="cms-legend__swatch" aria-hidden="true" />
              {series.name}
            </button>
          ))}
        </div>
      )}
      <svg
        className="cms-chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${spec.title}. ${summary}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {ticks.map((tick) => (
          <g key={tick} className="cms-grid">
            <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} />
            <text x={PAD.left - 8} y={y(tick)} dy="0.32em" textAnchor="end">
              {formatValue(tick, spec.unit)}
            </text>
          </g>
        ))}
        {spec.xLabels.map((label, index) =>
          index % labelEvery === 0 || index === spec.xLabels.length - 1 ? (
            <text
              key={label}
              className="cms-axis-label"
              x={x(index)}
              y={H - 12}
              textAnchor="middle"
            >
              {label}
            </text>
          ) : null,
        )}
        {spec.series.map((series, seriesIndex) => (
          <g
            key={series.name}
            className={`cms-series cms-w-c${seriesIndex + 1}`}
            data-series={seriesIndex}
          >
            <polyline
              fill="none"
              points={series.values.map((value, index) => `${x(index)},${y(value)}`).join(" ")}
            />
            {series.values.map((value, index) => (
              <circle
                key={index}
                cx={x(index)}
                cy={y(value)}
                r={4}
                data-tip={`${series.name} · ${spec.xLabels[index]}: ${formatValue(value, spec.unit)}`}
              >
                <title>{`${series.name}, ${spec.xLabels[index]}: ${formatValue(value, spec.unit)}`}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
      <DataTable
        columns={["", ...spec.series.map((s) => s.name)]}
        rows={spec.xLabels.map((label, index) => [
          label,
          ...spec.series.map((s) => formatValue(s.values[index] ?? 0, spec.unit)),
        ])}
      />
    </Frame>
  );
};

// ── Donut (SVG) ──────────────────────────────────────────────────────────────────────────────

const R = 70;
const CIRC = 2 * Math.PI * R;

const DonutChart = ({ spec }: { spec: Donut }) => {
  const total = spec.segments.reduce((sum, s) => sum + s.value, 0) || 1;
  let offset = 0;
  const share = (value: number) => `${Math.round((value / total) * 100)}%`;
  const largest = spec.segments.reduce((a, b) => (b.value > a.value ? b : a));

  return (
    <Frame spec={spec} source={spec.source}>
      <div className="cms-donut">
        <svg
          className="cms-donut__chart"
          viewBox="0 0 200 200"
          role="img"
          aria-label={`${spec.title}. ${spec.segments.map((s) => `${s.label} ${share(s.value)}`).join(", ")}`}
        >
          <g transform="rotate(-90 100 100)">
            {spec.segments.map((segment, index) => {
              const length = (segment.value / total) * CIRC;
              const circle = (
                <circle
                  key={segment.label}
                  className={`cms-donut__segment cms-w-c${index + 1}`}
                  cx={100}
                  cy={100}
                  r={R}
                  strokeDasharray={`${length} ${CIRC - length}`}
                  strokeDashoffset={-offset}
                  data-tip={`${segment.label}: ${formatValue(segment.value, spec.unit)} (${share(segment.value)})`}
                >
                  <title>{`${segment.label}: ${share(segment.value)}`}</title>
                </circle>
              );
              offset += length;
              return circle;
            })}
          </g>
          <text x="100" y="96" textAnchor="middle" className="cms-donut__big">
            {share(largest.value)}
          </text>
          <text x="100" y="118" textAnchor="middle" className="cms-donut__small">
            {largest.label.length > 18 ? `${largest.label.slice(0, 17)}…` : largest.label}
          </text>
        </svg>
        <ul className="cms-donut__legend">
          {spec.segments.map((segment, index) => (
            <li key={segment.label} className={`cms-w-c${index + 1}`}>
              <span className="cms-legend__swatch" aria-hidden="true" />
              <span className="cms-donut__label">{segment.label}</span>
              <span className="cms-donut__value">
                {formatValue(segment.value, spec.unit)} <span>({share(segment.value)})</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Frame>
  );
};

// ── Stats, table, comparison ─────────────────────────────────────────────────────────────────

const StatsPanel = ({ spec }: { spec: Stats }) => (
  <Frame spec={spec} source={spec.source}>
    <dl className="cms-stats">
      {spec.items.map((item) => (
        <div key={item.label} className="cms-stat">
          <dt className="cms-stat__label">{item.label}</dt>
          <dd className="cms-stat__value">{item.value}</dd>
        </div>
      ))}
    </dl>
  </Frame>
);

const DataTable = ({
  columns,
  rows,
  visible = false,
}: {
  columns: string[];
  rows: string[][];
  visible?: boolean;
}) => (
  <div className={visible ? "cms-table-wrap" : "cms-visually-hidden"}>
    <table className="cms-table">
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th key={`${column}-${index}`} scope="col">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) =>
              cellIndex === 0 ? (
                <th key={cellIndex} scope="row">
                  {cell}
                </th>
              ) : (
                <td key={cellIndex}>{cell}</td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TableWidget = ({ spec }: { spec: Table }) => (
  <Frame spec={spec} source={spec.source}>
    <DataTable columns={spec.columns} rows={spec.rows} visible />
  </Frame>
);

const ComparisonWidget = ({ spec }: { spec: Comparison }) => (
  <Frame spec={spec}>
    <div className="cms-compare">
      {(["left", "right"] as const).map((side) => (
        <div
          key={side}
          className={
            spec.recommended === side ? "cms-compare__col is-recommended" : "cms-compare__col"
          }
        >
          <p className="cms-compare__heading">
            {spec[side].heading}
            {spec.recommended === side && <span className="cms-compare__badge">Better</span>}
          </p>
          <ul>
            {spec[side].points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </Frame>
);

export const Widget = ({ spec }: { spec: WidgetSpec }) => {
  switch (spec.kind) {
    case "bar":
      return <BarChart spec={spec} />;
    case "line":
      return <LineChart spec={spec} />;
    case "donut":
      return <DonutChart spec={spec} />;
    case "stats":
      return <StatsPanel spec={spec} />;
    case "table":
      return <TableWidget spec={spec} />;
    case "comparison":
      return <ComparisonWidget spec={spec} />;
  }
};
